import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Container, Row, Col, Button, Spinner } from "reactstrap";
import styles from "../css/sessionDashboard.module.scss";
import favicon from "../../../assets/images/favicon.png";
import ip from "../../Pharmacy/IPinventory/css/ipInventory.module.scss";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import CommonButton from "Components/Common/CommonButton";
import CommonStatusBadge from "Components/Common/CommonStatusBadge";
import NewScanningSessionModal from "../components/NewScanningSessionModal";
import { SCANNING_PATHS } from "Routes/paths";
import { useScanningSessionService } from "modules/ScanningApp/api/useScanningSession";
import CommonSearch from "Components/Common/CommonSearch";
import { formatDateTime } from "helpers/crf.utils";
import { showToastMessage, userLogout, GetUserData } from "common/service/CommonService";
import FullPageLoader from "Components/Common/FullPageLoader";
import { accessioningStatsCards } from "../helper/scanningconstant";
import FilePreviewModal from "../../Study/Attachments/components/FilePreviewModal";
import ClinicalActivityLanding from "../components/ClinicalActivityLanding";
import { useGlobalStudy } from "../../../context/StudyContext";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import { useMasterStudyService } from "modules/Clinical/StudyDetails/api/useStudyQuery";
import { changeSidebarVisibility } from "../../../slices/thunks";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";
import { usePermission } from "../../../context/PermissionContext";
import AccessRestricted from "Components/Common/AccessRestricted";
import NoStudySelectedPlaceholder from "Components/Common/NoStudySelectedPlaceholder";

/* ------------------------------------------------------------------ */
/* Stat Card Component                                                */
/* ------------------------------------------------------------------ */
interface StatCardProps {
  title: string;
  value: number | string;
  subtext?: string;
  icon: React.ReactNode;
  subtextIcon?: string;
  subtextCls?: string;
  bgColor?: string;
  iconColor?: string;
  trendType?: "info" | "negative" | "success";
  isDark?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtext,
  icon,
  subtextIcon,
  bgColor = "#ecfdf5",
  iconColor = "#10b981",
  trendType = "info",
  isDark = false,
}) => (
  <div className={styles.statCard}>
    {!isDark && (
      <div
        className={styles.iconWrapper}
        style={{ backgroundColor: bgColor, color: iconColor }}
      >
        {icon}
      </div>
    )}
    <div className={styles.label}>{title}</div>
    <div className={styles.value}>{value}</div>
    {!isDark && subtext && (
      <div className={`${styles.trend} ${styles[trendType]}`}>
        <i className={subtextIcon ?? "ri-information-line"} />
        {subtext}
      </div>
    )}
  </div>
);

/* ------------------------------------------------------------------ */
/* Main Component                                                     */
/* ------------------------------------------------------------------ */
const ScannerPage: React.FC = () => {
  document.title = "Scanner | ZENTIXS VISTA";

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch: any = useDispatch();
  const { selectedStudy: globalStudy, setGlobalStudy } = useGlobalStudy();

  const handleLogout = () => {
    userLogout(navigate);
  };

  const stateSessionId = location.state?.viewingSessionId;

  const [searchText, setSearchText] = useState<string>("");
  const [currentPageNumber, setcurrentPageNumber] = useState<number>(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [flowSelected, setFlowSelected] = useState<"new" | "existing" | null>(() => {
    return sessionStorage.getItem("scanning_flow_selected") as "new" | "existing" | null;
  });
  const [viewingSession, setViewingSession] = useState<any>(() => {
    const saved = sessionStorage.getItem("scanning_viewing_session");
    return saved ? JSON.parse(saved) : null;
  });
  const [isNewSessionFlow, setIsNewSessionFlow] = useState<boolean>(false);
  const [selectedActivity, setSelectedActivity] = useState<"sample_collection" | "dosing" | null>(null);

  useEffect(() => {
    if (flowSelected) {
      sessionStorage.setItem("scanning_flow_selected", flowSelected);
    } else {
      sessionStorage.removeItem("scanning_flow_selected");
    }
  }, [flowSelected]);

  useEffect(() => {
    if (viewingSession) {
      sessionStorage.setItem("scanning_viewing_session", JSON.stringify(viewingSession));
      if (viewingSession.studyid && (!globalStudy || globalStudy.studyid !== viewingSession.studyid)) {
        setGlobalStudy({
          studyid: viewingSession.studyid,
          studyname: viewingSession.studycode,
        });
      }
    } else {
      sessionStorage.removeItem("scanning_viewing_session");
    }
  }, [viewingSession, globalStudy, setGlobalStudy]);

  // Responsive Width State
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowWidth <= 576;
  const isTablet = windowWidth <= 1024 && windowWidth > 576;
  const isDesktop = windowWidth > 1024;

  // Hide sidebar when opening/loading the page on mobile devices
  useEffect(() => {
    if (window.innerWidth <= 576) {
      dispatch(changeSidebarVisibility("hidden"));
      document.body.classList.remove("vertical-sidebar-enable");
    }
  }, [dispatch]);

  // Reset to page 1 whenever the global study filter changes
  const prevStudyRef = useRef(globalStudy?.studyid);
  useEffect(() => {
    if (prevStudyRef.current !== globalStudy?.studyid) {
      prevStudyRef.current = globalStudy?.studyid;
      setcurrentPageNumber(1);
    }
  }, [globalStudy?.studyid]);

  // APIs
  const targetStudyId = globalStudy?.studyid || viewingSession?.studyid;
  const { SelectAllMutation: AuditMutation } = useAuditService();
  const { SelectAllQuery, UpdateMutation } = useScanningSessionService({
    searchtext: searchText,
    currentpage: currentPageNumber,
    pagesize: 10,
    studyid: targetStudyId,
  });


  const { hasPermission } = usePermission();
  const { DropDownAllQuery: studySelectAllQuery } = useMasterStudyService({ enabledDropDown: true });
  const hasAllStudiesRight = hasPermission(PERMISSION_MODULES.GLOBAL_STUDIES, PERMISSION_ACTIONS.ALL_STUDIES);
  const user = GetUserData();

  const studyOptions: Array<{
    label: string | number;
    value: string | number;
  }> = hasAllStudiesRight
      ? (((studySelectAllQuery.data as any)?.data || []).map((study: any) => ({
        label: study.studycode,
        value: study.studyid,
      })))
      : ((user?.studies as any[])?.map((study: any) => ({
        label: study.studycode,
        value: study.studyid,
      })) || []);

  const rawData = SelectAllQuery?.data?.data || [];
  const summaryItem = rawData.find((item: any) => item.summarycard);
  const rawSessionList = rawData.filter((item: any) => !item.summarycard);

  useEffect(() => {
    if (stateSessionId) {
      if (viewingSession) {
        // Already have viewingSession (e.g. from sessionStorage), so we can safely clear the state
        navigate(location.pathname, { replace: true, state: {} });
      } else if (rawSessionList.length > 0) {
        const match = rawSessionList.find(
          (item: any) => Number(item.sessionid) === Number(stateSessionId)
        );
        if (match) {
          setGlobalStudy({
            studyid: match.studyid,
            studyname: match.studycode,
          });
          setViewingSession(match);
          setFlowSelected("existing");
          // Clear location state once we successfully match
          navigate(location.pathname, { replace: true, state: {} });
        }
      }
    }
  }, [stateSessionId, rawSessionList, viewingSession, setGlobalStudy, navigate, location.pathname]);

  // If viewing a specific session, show that session (prefer query list match, fallback to stored viewingSession)
  const sessionData = viewingSession
    ? (() => {
      const found = rawSessionList.find((item: any) => Number(item.sessionid) === Number(viewingSession.sessionid));
      return found ? [found] : [viewingSession];
    })()
    : rawSessionList;

  const summaryData = summaryItem?.summarycard || {
    total_subjects: 0,
    total_vaccutainers_scanned: 0,
    total_aliquots_scanned: 0,
    total_pending_aliquots: 0,
  };


  const onViewSession = (session: any) => {
    setGlobalStudy({
      studyid: session.studyid,
      studyname: session.studycode,
    });
    setViewingSession(session);
    setFlowSelected("existing");
    setModalOpen(false);
  };

  const onSearch = (value: string) => {
    setSearchText(value);
    setcurrentPageNumber(1);
  };

  const onAuditClick = () => {
    const payload = { modulename: "Session" };
    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], "Session_Audit_Log.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  const handleResume = (row: any) => {
    console.log("handleResume invoked with row:", row);
    const rawId = row?.sessionid || row?.session_id || row?.id || viewingSession?.sessionid || viewingSession?.session_id;
    const sessionIdNum = rawId ? Number(rawId) : null;

    if (!sessionIdNum || isNaN(sessionIdNum)) {
      showToastMessage("error", "Invalid or missing Session ID");
      console.error("handleResume error: Cannot find valid sessionid in row:", row);
      return;
    }

    const payload = {
      sessionid: sessionIdNum,
      sessionstate: "active",
      updatereason: "Update Session Status",
    };

    console.log("Resuming session with payload:", payload);

    UpdateMutation.mutate(payload, {
      onSuccess: (res: any) => {
        showToastMessage(
          "success",
          res?.message || "Session resumed successfully"
        );
        navigate(SCANNING_PATHS.SESSION.replace(":id", String(sessionIdNum)));
      },
      onError: (err: any) => {
        showToastMessage("error", err || "Failed to resume session");
      },
    });
  };

  // Columns for Desktop View Table
  const desktopColumns = [
    {
      key: "sessionid",
      label: "Session ID",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <p className={ip.cellPrimary}>
            {row.sessionno || row.sessionid || "-"}
          </p>
        );
      },
    },
    {
      key: "studycode",
      label: "Study Code",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return <p className={ip.cellPrimary}>{row.studycode || "-"}</p>;
      },
    },
    {
      key: "period_timepoint",
      label: "Period & Timepoint",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <div className="d-flex justify-content-center align-items-center gap-2">
            <span className="fw-semibold text-dark">
              P - {row.period_number || "-"}
            </span>
            <span className="fw-semibold text-dark">|</span>
            <span className="fw-semibold text-dark">
              T - {row.timepoint_number || "-"}
            </span>
          </div>
        );
      },
    },
    {
      key: "scanningmode",
      label: "Dosing Station",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <div className="d-flex justify-content-center align-items-center">
            <span className="badge bg-primary-subtle text-primary border border-primary border-opacity-25" style={{ fontSize: "11px", padding: "5px 10px", borderRadius: "6px" }}>
              <i className="ri-building-2-line me-1"></i>
              {`DS-${row.dosingstationno ? String(row.dosingstationno).padStart(2, "0") : "01"}`}
            </span>
          </div>
        );
      },
    },
    {
      key: "subjects",
      label: "Subjects",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <p
            className={ip.cellPrimary}
          >{`${row.completed_subjects || 0} / ${row.total_subjects || 0}`}</p>
        );
      },
    },
    {
      key: "vacutainers",
      label: "Vacutainers",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <p
            className={ip.cellPrimary}
          >{`${row.completed_vaccutainers || 0} / ${row.total_vaccutainers || 0}`}</p>
        );
      },
    },
    {
      key: "aliquots",
      label: "Aliquots",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <p
            className={ip.cellPrimary}
          >{`${row.completed_aliquots || 0} / ${row.total_aliquots_count || (row.total_aliquots * (row.totalsubjects || row.total_subjects || 0)) || 0}`}</p>
        );
      },
    },
    {
      key: "status",
      label: "Status",
      textCenter: true,
      render: (_: any, row?: any) => {
        if (!row) return null;
        const status = (row.display_status === "INACTIVE" || !row.display_status) ? "PAUSED" : row.display_status;

        let color = "secondary";
        if (status === "COMPLETED") color = "success";
        else if (status === "ACTIVE") color = "primary";
        else if (status === "PAUSED") color = "warning";
        else if (status === "ATTENTION") color = "danger";

        return (
          <div className="d-flex justify-content-center">
            <CommonStatusBadge
              isActive={true}
              activeText={status}
              activeColor={color}
              style={{ width: "85px" }}
            />
          </div>
        );
      },
    },
    {
      key: "lastactive",
      label: "Last Active",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <p className={ip.cellPrimary}>
            {row.last_active ? formatDateTime(row.last_active) : "-"}
          </p>
        );
      },
    },
    {
      key: "action",
      label: "Action",
      render: (_: any, row?: any) => {
        if (!row) return null;

        const status = (row.display_status === "INACTIVE" || !row.display_status) ? "PAUSED" : row.display_status;
        const action =
          status === "COMPLETED"
            ? "View Log"
            : status === "ATTENTION"
              ? "Resolve"
              : "Resume";

        const getIcon = (action: string) => {
          switch (action) {
            case "Resume":
              return "ri-play-line";
            case "Resolve":
              return "ri-shield-check-line";
            case "View Log":
              return "ri-file-list-3-line";
            default:
              return "ri-external-link-line";
          }
        };

        return (
          <div
            className="btn btn-outline-primary rounded-pill fw-semibold d-flex align-items-center justify-content-center gap-1"
            style={{
              width: "100px",
              padding: "2px 10px",
              fontSize: "12px",
              cursor: "pointer",
            }}
            onClick={() => {
              if (action === "View Log") {
                navigate(SCANNING_PATHS.LOGS.replace(":id", row.sessionid));
              } else if (action === "Resume") {
                handleResume(row);
              }
            }}
          >
            <i className={`${getIcon(action)} fs-13`} />
            {action}
          </div>
        );
      },
    },
  ];

  const formatSessionModeText = (sessionObj: any) => {
    if (!sessionObj) return "";
    const stationNo = sessionObj.dosingstationno ?? sessionObj.dosing_station_no ?? sessionObj.dosingStation;
    const formattedStation = stationNo ? String(stationNo).padStart(2, "0") : "01";
    return `DS-${formattedStation}`;
  };

  const renderStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    let iconClass = "ri-indeterminate-circle-fill";
    if (status === "COMPLETED") iconClass = "ri-checkbox-circle-fill";
    else if (status === "ACTIVE") iconClass = "ri-play-circle-fill";
    else if (status === "PAUSED") iconClass = "ri-pause-circle-fill";
    else if (status === "ATTENTION") iconClass = "ri-error-warning-fill";

    return (
      <span className={`${styles.statusBadge} ${styles[statusLower] || ""}`}>
        <i className={iconClass} />
        {status}
      </span>
    );
  };
  const isAuthorized = hasPermission(
    PERMISSION_MODULES.SESSION,
    `${PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER}, ${PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT}`
  );

  if (!isAuthorized) {
    return <AccessRestricted moduleName="Sessions" />;
  }

  const isQueryLoading = hasAllStudiesRight && studySelectAllQuery.isLoading;
  if (!isQueryLoading && studyOptions.length === 0) {
    return <NoStudySelectedPlaceholder moduleName="accessioning" />;
  }

  return (
    <React.Fragment>
      {!flowSelected ? (
        !selectedActivity ? (
          /* ── Screen 1: Production Grade Clinical Activity Landing (Image 2 style) ── */
          <ClinicalActivityLanding
            onSelectActivity={(act) => setSelectedActivity(act)}
            handleLogout={handleLogout}
          />
        ) : (
          /* ── Screen 2: Session Setup Screen (Image 3 style) ── */
          <React.Fragment>
            {isMobile ? (
              <header className={styles.mobileAppHeader}>
                <div className={styles.headerLeft}>
                  <button
                    type="button"
                    className="btn p-0 border-0 bg-transparent text-dark me-1"
                    onClick={() => setSelectedActivity(null)}
                    title="Back to Activities"
                  >
                    <i className="ri-arrow-left-line fs-20"></i>
                  </button>
                  <img src={favicon} alt="Vista Logo" className={styles.projectLogo} />
                  <h1 className={styles.headerTitle} style={{ fontSize: "14px" }}>
                    {selectedActivity === "dosing" ? "IP Dosing Session" : "Accessioning Session"}
                  </h1>
                </div>
                <div className={styles.headerRight}>
                  <button
                    type="button"
                    className="btn p-0 border-0 bg-transparent text-muted d-flex align-items-center justify-content-center"
                    style={{ outline: "none", boxShadow: "none" }}
                    onClick={handleLogout}
                    title="Logout"
                  >
                    <i className="ri-logout-box-r-line fs-20 text-danger" />
                  </button>
                </div>
              </header>
            ) : (
              <div className="d-flex align-items-center justify-content-between p-3 border-bottom bg-white" style={{ minHeight: "60px" }}>
                <div className="d-flex align-items-center gap-3">
                  <Button
                    color="light"
                    size="sm"
                    className="border text-dark d-flex align-items-center gap-1"
                    onClick={() => setSelectedActivity(null)}
                  >
                    <i className="ri-arrow-left-line"></i> Back to Activities
                  </Button>
                  <img src={favicon} alt="Vista Logo" style={{ height: "30px", width: "auto" }} />
                  <h5 className="mb-0 fw-bold text-dark">
                    {selectedActivity === "dosing" ? "Dose Administration Session Setup" : "Sample Collection Session Setup"}
                  </h5>
                </div>
                <div>
                  <Button
                    color="danger"
                    outline
                    size="sm"
                    className="rounded-pill d-flex align-items-center gap-1 px-3"
                    onClick={handleLogout}
                  >
                    <i className="ri-logout-box-r-line fs-14" />
                    Logout
                  </Button>
                </div>
              </div>
            )}
            <div style={{ minHeight: isMobile ? "calc(100vh - 60px)" : "calc(100vh - 120px)", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f8fafc", padding: "24px" }}>
              <div className="text-center w-100 m-auto" style={{ maxWidth: "480px" }}>
                <div className="mb-4">
                  <div
                    className={`d-inline-flex align-items-center justify-content-center p-4 rounded-circle mb-3 ${selectedActivity === "dosing"
                        ? "bg-success bg-opacity-10 text-success"
                        : "bg-primary bg-opacity-10 text-primary"
                      }`}
                    style={{
                      width: "120px",
                      height: "120px",
                      position: "relative",
                      boxShadow: selectedActivity === "dosing" ? "0 0 0 0 rgba(16, 185, 129, 0.4)" : "0 0 0 0 rgba(37, 99, 235, 0.4)",
                      animation: "logoPulse 2s infinite",
                    }}
                  >
                    <style dangerouslySetInnerHTML={{
                      __html: `
                      @keyframes logoPulse {
                        0% { box-shadow: 0 0 0 0 ${selectedActivity === "dosing" ? "rgba(16, 185, 129, 0.4)" : "rgba(37, 99, 235, 0.4)"}; }
                        70% { box-shadow: 0 0 0 12px ${selectedActivity === "dosing" ? "rgba(16, 185, 129, 0)" : "rgba(37, 99, 235, 0)"}; }
                        100% { box-shadow: 0 0 0 0 ${selectedActivity === "dosing" ? "rgba(16, 185, 129, 0)" : "rgba(37, 99, 235, 0)"}; }
                      }
                      @keyframes laserMove {
                        0%, 100% { transform: translateY(-5px); }
                        50% { transform: translateY(5px); }
                      }
                    `}} />
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="56" height="56" fill="currentColor">
                      <rect x="4.5" y="4" width="1" height="12" rx="0.5" />
                      <rect x="6.5" y="4" width="1" height="12" rx="0.5" />
                      <rect x="8.5" y="4" width="2" height="12" rx="0.5" />
                      <rect x="11.5" y="4" width="1" height="12" rx="0.5" />
                      <rect x="13.5" y="4" width="1.5" height="12" rx="0.5" />
                      <rect x="16" y="4" width="1" height="12" rx="0.5" />
                      <rect x="18.5" y="4" width="1" height="12" rx="0.5" />
                      <line x1="1" y1="10" x2="23" y2="10" stroke={selectedActivity === "dosing" ? "#10b981" : "#3b82f6"} strokeWidth="2" strokeLinecap="round" style={{ animation: "laserMove 2s infinite ease-in-out" }} />
                      <path d="M 3,6 L 3,3 L 6,3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      <path d="M 21,6 L 21,3 L 18,3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      <path d="M 3,14 L 3,17 L 6,17" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      <path d="M 21,14 L 21,17 L 18,17" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </div>
                  <h3 className="fw-bold text-dark mb-2">
                    {selectedActivity === "dosing" ? "Dose Administration Session" : "Accessioning Session"}
                  </h3>
                  <p className="text-muted fs-14">
                    Please select whether you want to initialize a brand new scanning session or view/resume an existing active session.
                  </p>
                </div>

                <div className="d-flex flex-column gap-3 w-100 mt-4">
                  <CommonButton
                    module={PERMISSION_MODULES.SESSION}
                    action={`${PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER} , ${PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT}`}
                    type="button"
                    className="btn py-3 px-4 d-flex align-items-center justify-content-between text-start border-0 shadow-sm"
                    style={{
                      borderRadius: "12px",
                      background: selectedActivity === "dosing" ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #2563eb, #1d4ed8)"
                    }}
                    onClick={() => {
                      setIsNewSessionFlow(true);
                      setModalOpen(true);
                    }}
                  >
                    <div>
                      <div className="fw-bold text-white fs-15">Create New Session</div>
                      <div className="text-white-50 fs-12 mt-1">Start scanning for a new study period</div>
                    </div>
                    <i className="ri-arrow-right-line text-white fs-20"></i>
                  </CommonButton>

                  <CommonButton
                    module={PERMISSION_MODULES.SESSION}
                    action={`${PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER} , ${PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT}`}
                    type="button"
                    className="btn btn-outline-primary py-3 px-4 d-flex align-items-center justify-content-between text-start bg-white"
                    style={{ borderRadius: "12px", border: "1px solid #cbd5e1", color: "#1e293b" }}
                    onClick={() => {
                      setIsNewSessionFlow(false);
                      setModalOpen(true);
                    }}
                  >
                    <div>
                      <div className="fw-bold text-dark fs-15">Existing Session</div>
                      <div className="text-muted fs-12 mt-1">View status or resume an active session</div>
                    </div>
                    <i className="ri-arrow-right-line text-primary fs-20"></i>
                  </CommonButton>
                </div>
              </div>
            </div>
          </React.Fragment>
        )
      ) : (
        /* ── Main Dashboard Content (Active once flow selected) ── */
        <React.Fragment>
          {/* Custom Mobile Header */}
          {isMobile && (
            <header className={styles.mobileAppHeader}>
              <div className={styles.headerLeft}>
                {viewingSession && (
                  <button
                    type="button"
                    className="btn p-0 border-0 bg-transparent text-dark d-flex align-items-center justify-content-center"
                    style={{ outline: "none", boxShadow: "none" }}
                    onClick={() => {
                      setFlowSelected(null);
                      setViewingSession(null);
                    }}
                  >
                    <i className="ri-arrow-left-line fs-22"></i>
                  </button>
                )}
                {/* Project Vista Logo */}
                <img src={favicon} alt="Vista Logo" className={styles.projectLogo} />
                <h1 className={styles.headerTitle} style={{ fontSize: viewingSession ? "14px" : "18px", fontWeight: 700 }}>
                  {viewingSession
                    ? `Studycode:${viewingSession.studycode} | P-${String(viewingSession.period_number).padStart(2, "0")} | T-${viewingSession.timepoint_number}${formatSessionModeText(viewingSession) ? ` | Mode:${formatSessionModeText(viewingSession)}` : ""}`
                    : "Dashboard"}
                </h1>
              </div>
              {!viewingSession && (
                <div className={styles.headerRight}>
                  <button
                    type="button"
                    className={styles.newSessionPill}
                    onClick={() => {
                      setIsNewSessionFlow(true);
                      setModalOpen(true);
                    }}
                  >
                    + New
                  </button>
                </div>
              )}
            </header>
          )}

          <div className={styles.dashboardContainer}>
            {UpdateMutation.isPending && (
              <FullPageLoader message="Updating Session Status..." />
            )}

            {/* ── Laptop / Desktop Layout ──────────────────────────────────── */}
            {isDesktop && (
              <Container fluid>
                {/* Page Header */}
                <div className="d-flex justify-content-between align-items-center mt-4 mb-3 px-2">
                  <div className="d-flex align-items-center gap-3">
                    {viewingSession && (
                      <Button
                        color="light"
                        outline
                        className="px-2 py-1 bg-white border"
                        onClick={() => {
                          setFlowSelected(null);
                          setViewingSession(null);
                        }}
                      >
                        <i className="ri-arrow-left-line text-dark"></i>
                      </Button>
                    )}
                    <div>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <h5 className={`mb-0 ${styles.pageHeaderTitle}`}>
                          {viewingSession
                            ? `Accessioning Session - studycode:${viewingSession.studycode} | P-${String(viewingSession.period_number).padStart(2, "0")} | T-${viewingSession.timepoint_number}${formatSessionModeText(viewingSession) ? ` | Mode:${formatSessionModeText(viewingSession)}` : ""}`
                            : "Accessioning Sessions"}
                        </h5>
                      </div>
                      <p className={styles.pageHeaderSubtitle}>
                        {viewingSession
                          ? `Currently viewing session ${viewingSession.sessionno || viewingSession.sessionid}`
                          : "Manage and monitor recent sample accessioning activities."}
                      </p>
                    </div>
                  </div>
                  <div className="d-flex gap-2 align-items-center">
                    <CommonButton
                      module={PERMISSION_MODULES.SESSION}
                      action={PERMISSION_ACTIONS.AUDIT}
                      color="primary"
                      size="sm"
                      onClick={onAuditClick}
                      disabled={AuditMutation.isPending}
                      outline
                      className="fw-600"
                      style={{ height: "35px" }}
                    >
                      {AuditMutation.isPending ? (
                        <Spinner size="sm" className="me-2" />
                      ) : (
                        <i className="ri-history-line me-1"></i>
                      )}
                      Audit
                    </CommonButton>
                    {!viewingSession && (
                      <CommonButton
                        color="primary"
                        outline
                        className="px-2 fw-600"
                        onClick={() => {
                          setIsNewSessionFlow(true);
                          setModalOpen(true);
                        }}
                      >
                        <i className="ri-add-line fs-18 me-1" /> New Accessioning
                        Session
                      </CommonButton>
                    )}
                  </div>
                </div>

                {/* Stat Cards */}
                <Row className="g-3 mb-3 px-1">
                  {accessioningStatsCards({
                    subjects: summaryData.total_subjects,
                    vacutainers: summaryData.total_vaccutainers_scanned,
                    aliquots: summaryData.total_aliquots_scanned,
                    pending: summaryData.total_pending_aliquots,
                  }).map((stat, idx) => (
                    <Col md={3} key={idx}>
                      <StatCard
                        title={stat.title}
                        value={stat.value}
                        icon={<i className={stat.icon} />}
                        bgColor={stat.bgColor}
                        iconColor={stat.iconColor}
                        trendType={stat.trendType as any}
                        subtextIcon={stat.subtextIcon}
                      />
                    </Col>
                  ))}
                </Row>

                {/* Table Card */}
                <div className={styles.listPanel}>
                  <div className={styles.panelHeader}>
                    <h2>
                      <i className="ri-test-tube-line me-2 text-primary fs-18" />
                      {viewingSession ? "Session Details" : "Recent Sessions"}
                    </h2>
                    <CommonSearch
                      onSearch={onSearch}
                      searchText={searchText}
                      placeholder="Search..."
                    />
                  </div>

                  <div className="px-4">
                    <CommonTableIndexWise
                      columns={desktopColumns}
                      data={sessionData}
                      loading={SelectAllQuery.isFetching}
                      pageSize={10}
                      currentPage={currentPageNumber}
                      totalRecords={sessionData[0]?.total_count || (viewingSession ? 1 : 0)}
                      onPageChange={setcurrentPageNumber}
                      actions={{}}
                    />
                  </div>
                </div>
              </Container>
            )}

            {/* ── Tablet Layout (<= 1024px & > 576px) ────────────────────────── */}
            {isTablet && (
              <div className={styles.tabletFrame}>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div className="d-flex align-items-center gap-3">
                    {viewingSession && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => {
                          setFlowSelected(null);
                          setViewingSession(null);
                        }}
                      >
                        <i className="ri-arrow-left-line"></i> Back
                      </button>
                    )}
                    <h5 className={styles.pageHeaderTitle}>
                      {viewingSession
                        ? `${viewingSession.studycode} | P-${viewingSession.period_number} | T-${viewingSession.timepoint_number}${formatSessionModeText(viewingSession) ? ` | Mode:${formatSessionModeText(viewingSession)}` : ""}`
                        : "Accessioning Sessions"}
                    </h5>
                  </div>
                  {!viewingSession && (
                    <div className={styles.tabletHeaderControls}>
                      <button
                        type="button"
                        className={styles.newSessionBtn}
                        onClick={() => {
                          setIsNewSessionFlow(true);
                          setModalOpen(true);
                        }}
                      >
                        + New Session
                      </button>
                    </div>
                  )}
                </div>

                {/* Stat Cards (2x2 grid, light theme) */}
                <div className={styles.statsGrid}>
                  {accessioningStatsCards({
                    subjects: summaryData.total_subjects,
                    vacutainers: summaryData.total_vaccutainers_scanned,
                    aliquots: summaryData.total_aliquots_scanned,
                    pending: summaryData.total_pending_aliquots,
                  }).map((stat, idx) => (
                    <StatCard
                      key={idx}
                      title={stat.title}
                      value={stat.value}
                      icon={<i className={stat.icon} />}
                      bgColor={stat.bgColor}
                      iconColor={stat.iconColor}
                      trendType={stat.trendType as any}
                      subtextIcon={stat.subtextIcon}
                    />
                  ))}
                </div>

                {/* Recent Sessions (Responsive Light themed 4-column Table) */}
                <div className={styles.listPanel}>
                  <div className={styles.panelHeader}>
                    <h2>{viewingSession ? "Session Details" : "Recent Sessions"}</h2>
                  </div>
                  <div className={styles.tableWrapper}>
                    <table className={styles.customResponsiveTable}>
                      <thead>
                        <tr>
                          <th>Session ID</th>
                          <th>Study</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sessionData.length === 0 ? (
                          <tr>
                            <td colSpan={4} className="text-center py-4 text-muted">
                              No sessions found.
                            </td>
                          </tr>
                        ) : (
                          sessionData.map((row: any) => {
                            const status = (row.display_status === "INACTIVE" || !row.display_status) ? "PAUSED" : row.display_status;
                            return (
                              <tr key={row.sessionid}>
                                <td className={styles.sessionNo}>
                                  <div>{row.sessionno || row.sessionid || "-"}</div>
                                  <div className={styles.sessionSubDetails}>
                                    P-{String(row.period_number).padStart(2, "0") || "-"} | T-{row.timepoint_number || "0.0"}
                                  </div>
                                </td>
                                <td className={styles.studyCode}>
                                  {row.studycode || "-"}
                                </td>
                                <td>
                                  <span
                                    className={`${styles.statusBadge} ${styles[status.toLowerCase()] || ""}`}
                                  >
                                    {status}
                                  </span>
                                </td>
                                <td>
                                  {status === "COMPLETED" ? (
                                    <CommonButton
                                      module={PERMISSION_MODULES.SESSION}
                                      action={`${PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER} , ${PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT}`}
                                      type="button"
                                      className={styles.viewLogBtn}
                                      onClick={() => {
                                        const rawId = row?.sessionid || row?.session_id || row?.id;
                                        if (rawId) {
                                          navigate(SCANNING_PATHS.LOGS.replace(":id", String(rawId)));
                                        } else {
                                          showToastMessage("error", "Session ID not found");
                                        }
                                      }}
                                    >
                                      <i className="ri-file-list-3-line fs-16" /> View Log
                                    </CommonButton>
                                  ) : (
                                    <CommonButton
                                      module={PERMISSION_MODULES.SESSION}
                                      action={`${PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER} , ${PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT}`}
                                      type="button"
                                      className={styles.responsiveResumeBtn}
                                      onClick={() => handleResume(row)}
                                    >
                                      Resume
                                    </CommonButton>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ── Mobile Layout (<= 576px) ─────────────────────────────────── */}
            {isMobile && (
              <div className={styles.tabletFrame}>
                {/* Stat Cards (2x2 grid, light theme) */}
                <div className={styles.statsGrid}>
                  <StatCard
                    title="TOTAL SUBJECTS"
                    value={summaryData.total_subjects}
                    icon={<i className="ri-group-line" />}
                    bgColor="#ecfdf5"
                    iconColor="#10b981"
                  />
                  <StatCard
                    title="VACUTAINERS"
                    value={summaryData.total_vaccutainers_scanned}
                    icon={<i className="ri-test-tube-line" />}
                    bgColor="#fef3c7"
                    iconColor="#d97706"
                  />
                  <StatCard
                    title="ALIQUOTS"
                    value={summaryData.total_aliquots_scanned}
                    icon={<i className="ri-flask-line" />}
                    bgColor="#ecfdf5"
                    iconColor="#10b981"
                  />
                  <StatCard
                    title="PENDING"
                    value={summaryData.total_pending_aliquots}
                    icon={<i className="ri-time-line" />}
                    bgColor="#fef3c7"
                    iconColor="#d97706"
                  />
                </div>

                {/* Blue primary "New Accessioning Session" button from Image 4 */}
                {!viewingSession && (
                  <button
                    type="button"
                    className={styles.mobileNewSessionButton}
                    onClick={() => {
                      setIsNewSessionFlow(true);
                      setModalOpen(true);
                    }}
                  >
                    New Accessioning Session
                  </button>
                )}

                {/* Recent Sessions Header */}
                <h5 className={styles.mobileRecentHeader}>
                  {viewingSession ? "Session Details" : "Recent Sessions"}
                </h5>

                {/* Session Cards list */}
                <div className={styles.mobileSessionList}>
                  {sessionData.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      No sessions found.
                    </div>
                  ) : (
                    sessionData.map((row: any) => {
                      const status = (row.display_status === "INACTIVE" || !row.display_status) ? "PAUSED" : row.display_status;
                      return (
                        <div
                          key={row.sessionid}
                          className={styles.mobileSessionCard}
                        >
                          <div className={styles.cardContent}>
                            <div className={styles.cardHeader}>
                              <span className={styles.sessionNo}>
                                {row.sessionno || row.sessionid || "-"}
                              </span>
                              {renderStatusBadge(status)}
                            </div>
                            <div className={styles.cardDetails}>
                              Studycode:{row.studycode || "-"} · P-
                              {String(row.period_number).padStart(2, "0") || "-"} | T-
                              {row.timepoint_number || "0.0"}
                              {formatSessionModeText(row) && (
                                <span className="ms-1 fw-bold text-primary">
                                  · {formatSessionModeText(row)}
                                </span>
                              )}
                            </div>
                          </div>
                          {status !== "COMPLETED" ? (
                            <div className={styles.cardActions}>
                              <CommonButton
                                module={PERMISSION_MODULES.SESSION}
                                action={`${PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER} , ${PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT}`}
                                type="button"
                                className={styles.mobileResumeBtn}
                                onClick={() => handleResume(row)}
                              >
                                <i className="ri-play-fill" /> Resume
                              </CommonButton>
                            </div>
                          ) : (
                            <div className={styles.cardActions}>
                              <CommonButton
                                module={PERMISSION_MODULES.SESSION}
                                action={`${PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER} , ${PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT}`}
                                type="button"
                                className={styles.viewLogBtn}
                                onClick={() => {
                                  const rawId = row?.sessionid || row?.session_id || row?.id;
                                  if (rawId) {
                                    navigate(SCANNING_PATHS.LOGS.replace(":id", String(rawId)));
                                  } else {
                                    showToastMessage("error", "Session ID not found");
                                  }
                                }}
                              >
                                <i className="ri-file-list-3-line fs-16" /> View Log
                              </CommonButton>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        </React.Fragment>
      )}

      {/* Modal for creating a session */}
      <NewScanningSessionModal
        isOpen={modalOpen}
        toggle={() => {
          setModalOpen(!modalOpen);
        }}
        redirectPathPattern={SCANNING_PATHS.SESSION}
        isNewSessionFlow={isNewSessionFlow}
        onViewSession={onViewSession}
        defaultCategory={selectedActivity || "sample_collection"}
      />

      {/* File Preview Modal for Audit logs */}
      {isPreviewOpen && (
        <FilePreviewModal
          file={previewFile}
          isOpen={isPreviewOpen}
          size={"xl"}
          onClose={() => {
            setIsPreviewOpen(false);
            setPreviewFile(null);
          }}
        />
      )}
    </React.Fragment>
  );
};

export default ScannerPage;